import {useParams} from "react-router-dom";
import {deleteComponent, setComponent} from "../../feature/project/componentsSlice";
import {useAppDispatch, useAppSelector} from "../../feature/tools";
import React, {createRef, Fragment, useEffect} from "react";
import {Image, Button, Flex, Space, Tag, Typography, Modal} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExpand, faFile, faMultiply, faMusic, faPencil, faTrash} from "@fortawesome/free-solid-svg-icons";
import Markdown from "react-markdown";
import {ViewComponentModal} from "./ViewComponentModal";
import {Component} from "../../feature/project/setting";
import {AddComponentModal} from "./AddComponentModal";
import {editorRoles, WithUserPermission} from "./WithUserPermission";

const {confirm} = Modal;

export const ComponentDetails = (props: {
    component?: Component,
    onAddComponent: (component: Component) => void,
    onClose: () => void
}) => {
    const dispatch = useAppDispatch();
    const {projectId, componentId} = useParams();
    const ref = createRef<HTMLDivElement>();
    const [showModal, setShowModal] = React.useState(false);
    const [showAddModal, setShowAddModal] = React.useState(false);
    const [hasTags, setHasTags] = React.useState(false);
    const [hasAssets, setHasAssets] = React.useState(false);
    const [hasReferences, setHasReferences] = React.useState(false);
    const component = useAppSelector(state => state.components.component)

    useEffect(() => {
        dispatch(setComponent(props.component))
    }, [props.component, dispatch])

    useEffect(() => {
        ref.current?.scrollTo(0, 0)
    }, [componentId])

    useEffect(() => {
        setHasTags(component?.tags?.length !== undefined && component?.tags?.length > 0)
        setHasAssets(component?.assets?.length !== undefined && component?.assets?.length > 0)
        setHasReferences(component?.references?.length !== undefined && component?.references?.length > 0)
    }, [component])

    const showDeleteConfirm = () => {
        confirm({
            title: 'Delete Component',
            content: 'Are you sure you want to delete this component?',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (!componentId || !projectId) return
                dispatch(deleteComponent({projectId: parseInt(projectId), componentId: parseInt(componentId)}))
                props.onClose()
            },
        });
    };

    return (
        <Flex style={{paddingLeft: '20px', height: "100%"}} vertical={true} gap={16}>
            <Flex justify={'space-between'} align={'center'}>
                {component?.category?.title}<Space>
                <WithUserPermission roles={editorRoles}>
                    <Space>
                        <Button type={'text'} onClick={() => setShowAddModal(true)}><FontAwesomeIcon
                            icon={faPencil}/></Button>
                        <Button type={'text'} onClick={showDeleteConfirm}><FontAwesomeIcon icon={faTrash}/></Button>
                    </Space>
                </WithUserPermission>
                <Button type={'text'} onClick={() => setShowModal(true)}><FontAwesomeIcon icon={faExpand}/></Button>
                <Button type={'text'} onClick={() => props.onClose()}><FontAwesomeIcon
                    icon={faMultiply}/></Button></Space>
            </Flex>
            <div style={{
                overflowY: 'auto', scrollbarWidth: "none",
                overflowX: 'visible',
                height: '100%',
                width: '100%'
            }} ref={ref}>
                <Space direction={'vertical'} style={{width: '100%'}}>
                    <Image className={'big-cover'} alt={component?.title} wrapperStyle={{display: "block"}}
                           src={component?.cover ? component?.cover : '/images/placeholder.png'}
                           style={{borderRadius: 8, width: '100%', height: 350, objectFit: 'cover'}}/>
                    <Typography style={{fontWeight: 700, fontSize: 18}}>{component?.title}</Typography>
                    <div>
                        <Markdown className={"markdown-text"}>{component?.description}</Markdown>
                    </div>
                    {hasAssets ? <>
                        <Typography style={{fontWeight: 700}}>Assets</Typography>
                        <Image.PreviewGroup>
                            <Flex gap={8} wrap={'wrap'}>{component?.assets?.map(asset =>
                                asset.content?.endsWith('.mp3') ? <Flex justify={'center'} align={'center'} style={{
                                        width: 60, height: 60, backgroundColor: "#eee",
                                        borderRadius: 6,
                                    }}><FontAwesomeIcon icon={faMusic} size={'lg'}/></Flex> :
                                    asset.content?.endsWith('.json') ?<Flex justify={'center'} align={'center'} style={{
                                        width: 60, height: 60, backgroundColor: "#eee",
                                        borderRadius: 6,
                                        }}> <FontAwesomeIcon icon={faFile} size={'lg'}/></Flex> :
                                        <Image key={asset.id} className={'small-cover'}
                                               src={asset.content ? asset?.content : '/images/placeholder.png'}
                                               style={{
                                                   width: 60,
                                                   height: 60,
                                                   borderRadius: 6,
                                                   objectFit: 'cover'
                                               }}/>
                            )}</Flex>
                        </Image.PreviewGroup>
                    </> : ''}
                    {hasTags ? <>
                        <Typography style={{fontWeight: 700}}>Tags</Typography>
                        <Flex style={{gap: '8px 0'}} wrap={'wrap'}>{component?.tags?.map(tag => <Tag
                            key={tag.id}>{tag.title}</Tag>)}</Flex>
                    </> : ''}
                    {hasReferences ? <>
                        <Typography style={{fontWeight: 700}}>References</Typography>
                        <Image.PreviewGroup>
                            <Flex gap={8} wrap={'wrap'}>{component?.references?.map(reference =>
                                <Image key={reference.id} className={'small-cover'}
                                       src={reference.content ? reference?.content : '/images/placeholder.png'}
                                       style={{
                                           width: 60,
                                           height: 60,
                                           borderRadius: 6,
                                           objectFit: 'cover'
                                       }}/>)}</Flex>
                        </Image.PreviewGroup>
                    </> : ''}
                </Space>
            </div>
            {component ?
                <Fragment>
                    <ViewComponentModal
                        component={component}
                        visible={showModal}
                        onCancel={() => setShowModal(false)}/>
                    <AddComponentModal
                        component={component}
                        visible={showAddModal}
                        onCancel={() => setShowAddModal(false)}
                        onSave={(component: Component) => {
                            setShowAddModal(false)
                            props.onAddComponent(component)
                        }}
                    />
                </Fragment> : ''}
        </Flex>
    );
}